import { z } from 'zod';

import { Button, useAppForm } from '@/shared/ui';

import type { PromptLibraryItem } from './types';

const schema = z.object({
  title: z.string().trim().min(1, 'Title is required'),
  prompt: z.string().trim().min(1, 'Prompt is required'),
});

type Props = {
  onCancel: () => void;
  onSubmitPrompt: (
    item: Pick<PromptLibraryItem, 'title' | 'prompt'>,
  ) => Promise<void>;
  defaultValues?: {
    title: string;
    prompt: string;
  };
  isSubmitting?: boolean;
  submitLabel?: string;
};

export default function PromptLibraryCreateDialogBody({
  onCancel,
  onSubmitPrompt,
  defaultValues = {
    title: '',
    prompt: '',
  },
  isSubmitting = false,
  submitLabel = 'Create prompt',
}: Props) {
  const form = useAppForm({
    defaultValues,
    validators: {
      onChange: schema,
    },
    onSubmit: ({ value }) => {
      return onSubmitPrompt({
        title: value.title.trim(),
        prompt: value.prompt.trim(),
      });
    },
  });

  return (
    <form.Root className="h-full flex flex-col" onSubmit={form.handleSubmit}>
      <div
        className="flex flex-col gap-6 py-5 h-full px-7"
        style={{ scrollbarGutter: 'stable', scrollbarWidth: 'thin' }}
      >
        <form.AppField name="title">
          {(field) => (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground">Title</span>
              <field.TextField
                autoFocus
                maxLength={80}
                placeholder="e.g. Weekly issue summary"
              />
            </div>
          )}
        </form.AppField>

        <form.AppField name="prompt">
          {(field) => (
            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium text-foreground">
                Prompt
              </span>
              <field.Textarea
                className="overflow-y-auto resize-none h-85"
                placeholder="Enter your prompt instruction..."
                rows={6}
              />
            </div>
          )}
        </form.AppField>
      </div>

      <div className="flex items-center justify-end gap-2 py-4 shrink-0 px-7">
        <Button onClick={onCancel} type="button" variant="outline">
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
        >
          {([canSubmit, formIsSubmitting]) => (
            <Button
              disabled={!canSubmit || formIsSubmitting || isSubmitting}
              isLoading={isSubmitting}
              type="submit"
            >
              {submitLabel}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </form.Root>
  );
}
